import type { AssaySnapshot, AssaySnapshotColumn, AssaySnapshotRow } from '@/api'
import type { ColumnStructure, Metadata, WideExtraction } from '@/api/extractions'
import type { AssayReviewCommand } from '@/components/project/review/assayReviewController'
import { naturalSampleCompare } from '@/utils/naturalSort'
import { newRequestId } from '@/utils/uuid'

export interface AssayWideProjection {
  wide: WideExtraction
  rowIds: string[]
  columnIds: string[]
}

export function cloneAssaySnapshot(snapshot: AssaySnapshot): AssaySnapshot {
  return JSON.parse(JSON.stringify(snapshot)) as AssaySnapshot
}

function numberOrNull(value: string | undefined): number | null {
  if (!value?.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function assaySnapshotToWide(
  snapshot: AssaySnapshot,
  fileId: string,
  rows: readonly AssaySnapshotRow[] = snapshot.rows,
): WideExtraction {
  const sampleColumns = snapshot.columns.filter((column) => column.role === 'sample_attribute')
  const analyteColumns = snapshot.columns.filter((column) => column.role === 'analyte')
  const sampleColumn = sampleColumns.find((column) => column.name === 'sample')
  const sampleAttributes = sampleColumns.filter((column) => column.name !== 'sample')

  return {
    metadata: {
      ...snapshot.metadata,
      file: snapshot.metadata.file ?? '',
      lab: snapshot.metadata.lab ?? null,
      cert: snapshot.metadata.cert ?? null,
      date: snapshot.metadata.date ?? null,
      work_order: snapshot.metadata.work_order ?? null,
      depth_unit: snapshot.metadata.depth_unit ?? null,
      columns: {},
    } as Metadata,
    sample_attributes: sampleAttributes.map((column) => column.name),
    columns: analyteColumns.map((column) => column.name),
    column_structures: Object.fromEntries(
      analyteColumns.map((column) => [column.name, (column.structure ?? {}) as ColumnStructure]),
    ),
    rows: rows.map((row) => {
      const values = new Map(row.cells.map((cell) => [cell.column_id, cell.value]))
      const attributes = Object.fromEntries(
        sampleAttributes.map((column) => [column.name, values.get(column.id) ?? '']),
      )
      return {
        sample: sampleColumn ? (values.get(sampleColumn.id) ?? '') : '',
        from: numberOrNull(attributes.from),
        to: numberOrNull(attributes.to),
        sample_attrs: attributes,
        values: Object.fromEntries(
          analyteColumns.map((column) => [column.name, values.get(column.id) ?? '']),
        ),
        source_page: row.source_anchor?.page ?? 1,
        source_cert: fileId,
      }
    }),
    correction_state: { cells: [], added_rows: [], added_columns: [] },
  }
}

interface IndexedAssayRow {
  row: AssaySnapshotRow
  sourceIndex: number
  sample: string
  sequenceNumber: bigint | null
}

function sampleSequenceNumber(sample: string): bigint | null {
  const match = /^[A-Za-z]+-?(\d+)$/u.exec(sample)
  return match ? BigInt(match[1]) : null
}

function sortDisplayRows(entries: IndexedAssayRow[]): AssaySnapshotRow[] {
  // Select one ordering for the whole partition so mixed formats compare consistently.
  const numericOrder = entries.every((entry) => !entry.sample || entry.sequenceNumber !== null)
  return entries
    .sort((left, right) => {
      if (!left.sample || !right.sample) {
        if (!left.sample && !right.sample) return left.sourceIndex - right.sourceIndex
        return left.sample ? -1 : 1
      }
      if (numericOrder && left.sequenceNumber !== null && right.sequenceNumber !== null) {
        if (left.sequenceNumber < right.sequenceNumber) return -1
        if (left.sequenceNumber > right.sequenceNumber) return 1
        return left.sourceIndex - right.sourceIndex
      }
      return naturalSampleCompare(left.sample, right.sample) || left.sourceIndex - right.sourceIndex
    })
    .map((entry) => entry.row)
}

function displayRows(
  snapshot: AssaySnapshot,
  rowDisplayOrder: string | null | undefined,
): readonly AssaySnapshotRow[] {
  if (rowDisplayOrder !== 'sample_natural_within_page') return snapshot.rows
  const sampleColumn = snapshot.columns.find(
    (column) => column.role === 'sample_attribute' && column.name === 'sample',
  )
  if (!sampleColumn) return snapshot.rows
  const subtableColumn = snapshot.columns.find(
    (column) => column.name.trim().toLowerCase() === 'sub_table_index',
  )
  const partitions = new Map<string, IndexedAssayRow[]>()
  snapshot.rows.forEach((row, sourceIndex) => {
    const values = new Map(row.cells.map((cell) => [cell.column_id, cell]))
    const sampleCell = values.get(sampleColumn.id)
    const page = row.source_anchor?.page ?? sampleCell?.source_anchor?.page ?? null
    const subtable = subtableColumn ? (values.get(subtableColumn.id)?.value.trim() ?? '') : ''
    const partition = page === null ? `missing:${sourceIndex}` : JSON.stringify([page, subtable])
    const entries = partitions.get(partition) ?? []
    const sample = sampleCell?.value.trim() ?? ''
    entries.push({ row, sourceIndex, sample, sequenceNumber: sampleSequenceNumber(sample) })
    partitions.set(partition, entries)
  })
  return [...partitions.values()].flatMap(sortDisplayRows)
}

export function projectAssaySnapshot(
  snapshot: AssaySnapshot,
  fileId: string,
  rowDisplayOrder: string | null | undefined = 'source',
  rowOrder?: readonly string[] | null,
): AssayWideProjection {
  const sampleColumns = snapshot.columns.filter((column) => column.role === 'sample_attribute')
  const sampleColumn = sampleColumns.find((column) => column.name === 'sample')
  const sampleAttributes = sampleColumns.filter((column) => column.name !== 'sample')
  const analyteColumns = snapshot.columns.filter((column) => column.role === 'analyte')
  const rows = rowOrder
    ? rowsInReviewOrder(snapshot, rowOrder)
    : displayRows(snapshot, rowDisplayOrder)
  return {
    wide: assaySnapshotToWide(snapshot, fileId, rows),
    rowIds: rows.map((row) => row.id),
    columnIds: [
      ...(sampleColumn ? [sampleColumn.id] : []),
      ...sampleAttributes.map((column) => column.id),
      ...analyteColumns.map((column) => column.id),
    ],
  }
}

export function rowsInReviewOrder(
  snapshot: AssaySnapshot,
  rowOrder: readonly string[],
): AssaySnapshotRow[] {
  const byId = new Map(snapshot.rows.map((row) => [row.id, row]))
  const rows: AssaySnapshotRow[] = []
  for (const id of rowOrder) {
    const row = byId.get(id)
    if (row) {
      rows.push(row)
      byId.delete(id)
    }
  }
  return [...rows, ...byId.values()]
}

export function completeAssayRowOrder(
  snapshot: AssaySnapshot,
  rowOrder: readonly string[],
  pendingRowIds: readonly string[],
  selectedRowIds: readonly string[],
  displayOrder: string | null | undefined,
): { rowOrder: string[]; pendingRowIds: string[]; changed: boolean } {
  const rows = rowsInReviewOrder(snapshot, rowOrder)
  const sampleColumn = snapshot.columns.find(
    (column) => column.role === 'sample_attribute' && column.name === 'sample',
  )
  if (displayOrder !== 'sample_natural_within_page' || !sampleColumn) {
    return { rowOrder: rows.map((row) => row.id), pendingRowIds: [], changed: false }
  }
  const selected = new Set(selectedRowIds)
  const pending = new Set(pendingRowIds)
  const subtableColumn = snapshot.columns.find(
    (column) => column.name.trim().toLowerCase() === 'sub_table_index',
  )
  const samples = new Map(
    rows.map((row) => [
      row.id,
      row.cells.find((cell) => cell.column_id === sampleColumn.id)?.value.trim() ?? '',
    ]),
  )
  const ready = new Set(
    rows
      .filter((row) => pending.has(row.id) && !selected.has(row.id) && samples.get(row.id))
      .map((row) => row.id),
  )
  const partitions = new Map<
    string,
    { indexes: number[]; entries: IndexedAssayRow[]; affected: boolean }
  >()
  rows.forEach((row, sourceIndex) => {
    const sampleCell = row.cells.find((cell) => cell.column_id === sampleColumn.id)
    const page = row.source_anchor?.page ?? sampleCell?.source_anchor?.page ?? null
    const subtable =
      row.cells.find((cell) => cell.column_id === subtableColumn?.id)?.value.trim() ?? ''
    const key = page === null ? `missing:${row.id}` : JSON.stringify([page, subtable])
    const partition = partitions.get(key) ?? { indexes: [], entries: [], affected: false }
    partition.affected ||= ready.has(row.id)
    const sample = samples.get(row.id) ?? ''
    // Unfinished rows keep their exact slots, even when another row finishes.
    if (sample && (!pending.has(row.id) || ready.has(row.id))) {
      partition.indexes.push(sourceIndex)
      partition.entries.push({
        row,
        sourceIndex,
        sample,
        sequenceNumber: sampleSequenceNumber(sample),
      })
    }
    partitions.set(key, partition)
  })
  const nextOrder = rows.map((row) => row.id)
  for (const partition of partitions.values()) {
    if (!partition.affected) continue
    sortDisplayRows(partition.entries).forEach((row, index) => {
      nextOrder[partition.indexes[index]] = row.id
    })
  }
  return {
    rowOrder: nextOrder,
    pendingRowIds: rows
      .filter((row) => pending.has(row.id) && !ready.has(row.id))
      .map((row) => row.id),
    changed: nextOrder.some((id, index) => id !== rows[index].id),
  }
}

export function setSnapshotCell(
  snapshot: AssaySnapshot,
  rowId: string,
  columnId: string,
  value: string,
): AssaySnapshot {
  return updateSnapshotCells(snapshot, [{ rowId, columnId, value }])
}

export interface AssaySnapshotCellUpdate {
  rowId: string
  columnId: string
  value: string
}

export function updateSnapshotCells(
  snapshot: AssaySnapshot,
  updates: readonly AssaySnapshotCellUpdate[],
): AssaySnapshot {
  const next = cloneAssaySnapshot(snapshot)
  const cellsByRow = new Map(
    next.rows.map((row) => [row.id, new Map(row.cells.map((cell) => [cell.column_id, cell]))]),
  )
  for (const update of updates) {
    const cell = cellsByRow.get(update.rowId)?.get(update.columnId)
    if (!cell) throw new Error('The selected assay cell no longer exists.')
    cell.value = update.value
  }
  return next
}

export function insertSnapshotRow(
  snapshot: AssaySnapshot,
  index: number,
  sourcePage: number,
  sourceRowId?: string,
): AssaySnapshot {
  if (!Number.isInteger(sourcePage) || sourcePage < 1) {
    throw new Error('Choose a source PDF page before inserting an assay row.')
  }
  if (!snapshot.provenance_pages.some((page) => page.page === sourcePage)) {
    throw new Error('The selected source PDF page is unavailable for this assay.')
  }
  const sourceRow = snapshot.rows.find((row) => row.id === sourceRowId)
  const subtableColumn = snapshot.columns.find(
    (column) => column.name.trim().toLowerCase() === 'sub_table_index',
  )
  const sampleColumn = snapshot.columns.find(
    (column) => column.role === 'sample_attribute' && column.name === 'sample',
  )
  const rowPage =
    sourceRow?.source_anchor?.page ??
    sourceRow?.cells.find((cell) => cell.column_id === sampleColumn?.id)?.source_anchor?.page
  const subtable =
    rowPage === sourcePage
      ? sourceRow?.cells.find((cell) => cell.column_id === subtableColumn?.id)?.value
      : undefined
  const next = cloneAssaySnapshot(snapshot)
  next.rows.splice(Math.max(0, Math.min(index, next.rows.length)), 0, {
    id: newRequestId(),
    source_anchor: { page: sourcePage, evidence_refs: [] },
    // Keep the selected table's grouping key, without borrowing its data or evidence.
    cells: next.columns.map((column) => ({
      column_id: column.id,
      value: column.id === subtableColumn?.id ? (subtable ?? '') : '',
    })),
  })
  return next
}

export function deleteSnapshotRows(
  snapshot: AssaySnapshot,
  rowIds: ReadonlySet<string>,
): AssaySnapshot {
  const next = cloneAssaySnapshot(snapshot)
  next.rows = next.rows.filter((row) => !rowIds.has(row.id))
  return next
}

type NewSnapshotColumn = Pick<AssaySnapshotColumn, 'name' | 'role' | 'structure'>

function validatedColumnName(
  snapshot: AssaySnapshot,
  name: string,
  ignoredColumnId?: string,
): string {
  const normalized = name.trim()
  if (!normalized) throw new Error('Column names cannot be blank.')
  if (
    snapshot.columns.some((column) => column.id !== ignoredColumnId && column.name === normalized)
  ) {
    throw new Error('Column names must be unique.')
  }
  return normalized
}

export function insertSnapshotColumn(
  snapshot: AssaySnapshot,
  index: number,
  input: NewSnapshotColumn,
): AssaySnapshot {
  const name = validatedColumnName(snapshot, input.name)
  const next = cloneAssaySnapshot(snapshot)
  const insertionIndex = Math.max(0, Math.min(index, next.columns.length))
  const column: AssaySnapshotColumn = {
    id: newRequestId(),
    name,
    role: input.role,
    structure: cloneAssaySnapshotValue(input.structure ?? {}),
  }
  next.columns.splice(insertionIndex, 0, column)
  for (const row of next.rows) {
    row.cells.splice(insertionIndex, 0, { column_id: column.id, value: '' })
  }
  return next
}

export function renameSnapshotColumn(
  snapshot: AssaySnapshot,
  columnId: string,
  name: string,
): AssaySnapshot {
  const normalized = validatedColumnName(snapshot, name, columnId)
  const next = cloneAssaySnapshot(snapshot)
  const column = next.columns.find((candidate) => candidate.id === columnId)
  if (!column) throw new Error('The selected assay column no longer exists.')
  column.name = normalized
  return next
}

export function deleteSnapshotColumns(
  snapshot: AssaySnapshot,
  columnIds: ReadonlySet<string>,
): AssaySnapshot {
  const next = cloneAssaySnapshot(snapshot)
  next.columns = next.columns.filter((column) => !columnIds.has(column.id))
  for (const row of next.rows) {
    row.cells = row.cells.filter((cell) => !columnIds.has(cell.column_id))
  }
  return next
}

function cloneAssaySnapshotValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export function replaceSnapshotMetadata(
  snapshot: AssaySnapshot,
  metadata: AssaySnapshot['metadata'],
): AssaySnapshot {
  const next = cloneAssaySnapshot(snapshot)
  next.metadata = cloneAssaySnapshotValue(metadata)
  return next
}

export function snapshotsEqual(left: AssaySnapshot | null, right: AssaySnapshot | null): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

function mappedId(ids: readonly string[], index: number, kind: 'row' | 'column'): string {
  const id = ids[index]
  if (!id) throw new Error(`The selected assay ${kind} no longer exists.`)
  return id
}

function assertPresent(snapshot: AssaySnapshot, id: string, kind: 'row' | 'column'): void {
  const present =
    kind === 'row'
      ? snapshot.rows.some((row) => row.id === id)
      : snapshot.columns.some((column) => column.id === id)
  if (!present) throw new Error(`The selected assay ${kind} no longer exists.`)
}

function insertionIndex(
  snapshot: AssaySnapshot,
  projection: AssayWideProjection,
  index: number,
  role: AssaySnapshotColumn['role'],
): number {
  if (!Number.isInteger(index) || index < 0 || index > projection.columnIds.length) {
    throw new Error('The selected assay column no longer exists.')
  }
  if (index === 0) throw new Error('New assay columns must be inserted after the sample column.')
  const byId = new Map(snapshot.columns.map((column) => [column.id, column]))
  const leadingCount = projection.wide.sample_attributes?.length ?? 0
  const firstAnalyteIndex = leadingCount + 1
  if (
    (role === 'sample_attribute' && index > firstAnalyteIndex) ||
    (role === 'analyte' && index < firstAnalyteIndex)
  ) {
    throw new Error('The selected assay column position does not match its role.')
  }
  const beforeId = projection.columnIds
    .slice(index)
    .find((columnId) => byId.get(columnId)?.role === role)
  if (beforeId) {
    const nativeIndex = snapshot.columns.findIndex((column) => column.id === beforeId)
    if (nativeIndex < 0) throw new Error('The selected assay column no longer exists.')
    return nativeIndex
  }
  let lastSameRole = -1
  snapshot.columns.forEach((column, columnIndex) => {
    if (column.role === role) lastSameRole = columnIndex
  })
  return lastSameRole < 0 ? snapshot.columns.length : lastSameRole + 1
}

function applyMetadata(snapshot: AssaySnapshot, metadata: Metadata): AssaySnapshot {
  const next = replaceSnapshotMetadata(snapshot, {
    ...snapshot.metadata,
    ...metadata,
    columns: cloneAssaySnapshotValue(metadata.columns ?? {}),
  })
  const structures = metadata.columns ?? {}
  for (const column of next.columns) {
    if (column.role === 'analyte') {
      const edited = structures[column.name]
      column.structure = edited
        ? cloneAssaySnapshotValue({ ...(column.structure ?? {}), ...edited })
        : {}
    }
  }
  return next
}

function insertReviewRow(
  snapshot: AssaySnapshot,
  projection: AssayWideProjection,
  command: Extract<AssayReviewCommand, { type: 'insert-row' }>,
): AssaySnapshot {
  if (
    !Number.isInteger(command.index) ||
    command.index < 0 ||
    command.index > projection.rowIds.length
  ) {
    throw new Error('The selected assay row no longer exists.')
  }
  const beforeId = projection.rowIds[command.index]
  const index = beforeId
    ? snapshot.rows.findIndex((row) => row.id === beforeId)
    : snapshot.rows.length
  if (index < 0) throw new Error('The selected assay row no longer exists.')
  const sourceRowId =
    command.sourceRowIndex === undefined
      ? beforeId
      : mappedId(projection.rowIds, command.sourceRowIndex, 'row')
  return insertSnapshotRow(snapshot, index, command.sourcePage, sourceRowId)
}

export function applyAssayReviewCommand(
  snapshot: AssaySnapshot,
  projection: AssayWideProjection,
  command: AssayReviewCommand,
): AssaySnapshot {
  if (command.type === 'set-cells') {
    return updateSnapshotCells(
      snapshot,
      command.changes.map((change) => {
        const rowId = mappedId(projection.rowIds, change.rowIndex, 'row')
        const columnId = mappedId(projection.columnIds, change.columnIndex, 'column')
        assertPresent(snapshot, rowId, 'row')
        assertPresent(snapshot, columnId, 'column')
        return { rowId, columnId, value: change.value }
      }),
    )
  }
  if (command.type === 'insert-row') {
    return insertReviewRow(snapshot, projection, command)
  }
  if (command.type === 'delete-rows') {
    const rowIds = new Set(
      command.indexes.map((index) => {
        const rowId = mappedId(projection.rowIds, index, 'row')
        assertPresent(snapshot, rowId, 'row')
        return rowId
      }),
    )
    return deleteSnapshotRows(snapshot, rowIds)
  }
  if (command.type === 'insert-column') {
    return insertSnapshotColumn(
      snapshot,
      insertionIndex(snapshot, projection, command.index, command.role),
      {
        name: command.name,
        role: command.role,
        structure: command.structure ?? {},
      },
    )
  }
  if (command.type === 'rename-column') {
    const columnId = mappedId(projection.columnIds, command.index, 'column')
    assertPresent(snapshot, columnId, 'column')
    const next = renameSnapshotColumn(snapshot, columnId, command.name)
    const column = next.columns.find((candidate) => candidate.id === columnId)
    if (!column) throw new Error('The selected assay column no longer exists.')
    column.role = command.role
    column.structure = cloneAssaySnapshotValue(command.structure ?? {})
    return next
  }
  if (command.type === 'delete-columns') {
    const columnIds = new Set(
      command.indexes.map((index) => {
        if (index === 0) throw new Error('The sample column cannot be removed.')
        const columnId = mappedId(projection.columnIds, index, 'column')
        assertPresent(snapshot, columnId, 'column')
        return columnId
      }),
    )
    return deleteSnapshotColumns(snapshot, columnIds)
  }
  return applyMetadata(snapshot, command.metadata)
}
